import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import {
  ADMIN_DASHBOARD_FACILITIES,
  ADMIN_MARGIN_ACCOUNTS,
  ADMIN_MARGIN_ACCOUNT_STATS,
  ADMIN_LENDING_POOLS,
  ADMIN_POOL_STATS,
} from '../data/mockAdapter';

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon, badges, trend }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  badges?: { label: string; className: string }[];
  trend?: { label: string; className: string };
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
      {trend && <div className={`mt-1 text-xs font-medium ${trend.className}`}>{trend.label}</div>}
      {badges && (
        <div className="mt-2 flex flex-wrap gap-1.5 sm:gap-2">
          {badges.map((badge) => <span key={badge.label} className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}>{badge.label}</span>)}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function healthFactorColor(hf: number): string {
  if (hf >= 1.5) return 'text-green-600';
  if (hf >= 1.2) return 'text-yellow-600';
  return 'text-red-600';
}

function fmtCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

// ─── Page Component ──────────────────────────────────────────────────────────

export function DashboardPage() {
  const facilities = ADMIN_DASHBOARD_FACILITIES;

  const totalTvl = useMemo(() => facilities.reduce((sum, f) => sum + parseFloat(f.tvl.replace(/[$M]/g, '')), 0), [facilities]);
  const activeFacilities = useMemo(() => facilities.filter((f) => f.status === 'active').length, [facilities]);
  const totalDefaults = useMemo(() => facilities.reduce((sum, f) => sum + f.defaults, 0), [facilities]);

  const warnAccounts = useMemo(() => ADMIN_MARGIN_ACCOUNTS.filter((a) => a.status === 'warn').length, []);
  const frozenAccounts = useMemo(() => ADMIN_MARGIN_ACCOUNTS.filter((a) => a.status === 'frozen').length, []);

  const topPoolsByUtilization = useMemo(
    () => [...ADMIN_LENDING_POOLS].sort((a, b) => b.utilizationPct - a.utilizationPct).slice(0, 5),
    [],
  );

  const lowHealthAccounts = useMemo(
    () => ADMIN_MARGIN_ACCOUNTS.filter((a) => a.healthFactor > 0 && a.healthFactor < 1.5).sort((a, b) => a.healthFactor - b.healthFactor).slice(0, 8),
    [],
  );

  // Top-N for the 3-column visual
  const recentAccounts = useMemo(
    () => [...ADMIN_MARGIN_ACCOUNTS].sort((a, b) => b.collateralValue - a.collateralValue).slice(0, 5),
    [],
  );

  const topFacilitiesByTvl = useMemo(
    () =>
      [...facilities]
        .sort((a, b) => parseFloat(b.tvl.replace(/[$M]/g, '')) - parseFloat(a.tvl.replace(/[$M]/g, '')))
        .slice(0, 5),
    [facilities],
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-dashboard-layout-3-app-application-dashboard-home-layout" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Platform health and operational overview.</p>
        </div>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total TVL"
          value={`$${totalTvl.toFixed(1)}M`}
          subtitle={`${activeFacilities} active facilities`}
          icon={<Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Accounts"
          value={String(ADMIN_MARGIN_ACCOUNT_STATS.totalAccounts)}
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
          badges={[
            ...(warnAccounts > 0 ? [{ label: `${warnAccounts} WARN`, className: 'bg-yellow-100 text-yellow-700' }] : []),
            ...(frozenAccounts > 0 ? [{ label: `${frozenAccounts} FROZEN`, className: 'bg-blue-100 text-blue-700' }] : []),
            ...(totalDefaults > 0 ? [{ label: `${totalDefaults} DEFAULT`, className: 'bg-red-100 text-red-700' }] : []),
          ]}
        />
        <StatCard
          title="Total Borrowed"
          value={fmtCurrency(ADMIN_MARGIN_ACCOUNT_STATS.totalBorrowed)}
          subtitle={`${fmtCurrency(ADMIN_MARGIN_ACCOUNT_STATS.totalCollateral)} collateral`}
          icon={<Icon name="streamline:money-cash-bag-dollar-bag-payment-cash-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Avg Health Factor"
          value={ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor.toFixed(2)}
          icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />}
          trend={{
            label: ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor >= 1.5 ? 'Healthy' : ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor >= 1.2 ? 'Caution' : 'Critical',
            className: ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor >= 1.5 ? 'text-green-600' : ADMIN_MARGIN_ACCOUNT_STATS.avgHealthFactor >= 1.2 ? 'text-yellow-600' : 'text-red-600',
          }}
        />
      </div>

      {/* 3-column visual: Margin Accounts | Credit Facilities | Pools.
          Per Dennis (April 28): "paints a nice visual" connecting users
          → facilities → pools at first glance. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-6">
        {/* Column 1: Margin Accounts */}
        <div className="rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-baseline justify-between mb-1">
            <div className="flex items-center gap-2">
              <Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Margin Accounts</h2>
            </div>
            <Link to="/margin-accounts" className="text-xs text-muted-foreground hover:text-foreground">View all →</Link>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {ADMIN_MARGIN_ACCOUNT_STATS.totalAccounts} total
            {warnAccounts > 0 && ` · ${warnAccounts} at-risk`}
          </p>
          <div className="space-y-2">
            {recentAccounts.map((acct) => (
              <Link
                key={acct.id}
                to={`/margin-accounts/${acct.id}`}
                className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2 text-sm hover:bg-accent/30 transition-colors"
              >
                <div className="flex flex-col min-w-0">
                  <span className="font-mono text-xs truncate">{acct.id}</span>
                  <span className="text-[11px] text-muted-foreground truncate">{acct.facilityName}</span>
                </div>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${healthFactorColor(acct.healthFactor)} bg-muted/60`}>
                  HF {acct.healthFactor.toFixed(2)}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Column 2: Credit Facilities */}
        <div className="rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-baseline justify-between mb-1">
            <div className="flex items-center gap-2">
              <Icon name="streamline:building-2" className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Credit Facilities</h2>
            </div>
            <Link to="/credit-facilities" className="text-xs text-muted-foreground hover:text-foreground">View all →</Link>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {facilities.length} total · {activeFacilities} active
            {totalDefaults > 0 && ` · ${totalDefaults} defaults`}
          </p>
          <div className="space-y-2">
            {topFacilitiesByTvl.map((f) => (
              <Link
                key={f.id}
                to={`/cf/${encodeURIComponent(f.name)}`}
                className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2 text-sm hover:bg-accent/30 transition-colors"
              >
                <span className="font-medium truncate mr-2">{f.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[11px] text-muted-foreground">{f.tvl}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      f.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : f.status === 'paused'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {f.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Column 3: Stablecoin Pools */}
        <div className="rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-baseline justify-between mb-1">
            <div className="flex items-center gap-2">
              <Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Stablecoin Pools</h2>
            </div>
            <Link to="/stablecoin-pools" className="text-xs text-muted-foreground hover:text-foreground">View all →</Link>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            {ADMIN_LENDING_POOLS.length} pools · {ADMIN_POOL_STATS.avgUtilization.toFixed(1)}% avg utilization
          </p>
          <div className="space-y-2">
            {topPoolsByUtilization.map((pool) => (
              <Link
                key={pool.id}
                to={`/stablecoin-pools/${pool.id}`}
                className="block space-y-1.5 rounded-md border border-border/50 px-3 py-2 hover:bg-accent/30 transition-colors"
              >
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate mr-2">{pool.name}</span>
                  <span
                    className={`text-[11px] font-semibold ${
                      pool.utilizationPct > 90
                        ? 'text-red-600'
                        : pool.utilizationPct > 80
                        ? 'text-yellow-600'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {pool.utilizationPct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={`h-full ${
                      pool.utilizationPct > 90
                        ? 'bg-red-500'
                        : pool.utilizationPct > 80
                        ? 'bg-yellow-500'
                        : 'bg-primary'
                    }`}
                    style={{ width: `${Math.min(pool.utilizationPct, 100)}%` }}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* At-Risk Accounts drill-down (kept below as detail surface) */}
      {lowHealthAccounts.length > 0 && (
        <div className="rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="streamline:warning-triangle" className="h-5 w-5 text-yellow-500" />
              <h2 className="text-lg font-semibold">At-Risk Accounts</h2>
            </div>
            <Link to="/liquidation" className="flex items-center gap-1 text-xs text-primary hover:underline">
              View all <Icon name="streamline:interface-arrows-button-right-arrow-right-keyboard" className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {lowHealthAccounts.map((acct) => (
              <Link
                key={acct.id}
                to={`/margin-accounts/${acct.id}`}
                className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 hover:bg-accent/30 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-mono text-xs truncate">{acct.id}</div>
                  <div className="text-xs text-muted-foreground truncate">{acct.facilityName}</div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Collateral</div>
                    <div className="text-sm font-medium">{fmtCurrency(acct.collateralValue)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Borrowed</div>
                    <div className="text-sm font-medium">{fmtCurrency(acct.borrowed)}</div>
                  </div>
                  <div className="text-right w-12">
                    <div className="text-xs text-muted-foreground">HF</div>
                    <div className={`text-sm font-bold ${healthFactorColor(acct.healthFactor)}`}>{acct.healthFactor.toFixed(2)}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                    acct.status === 'warn' ? 'bg-yellow-100 text-yellow-700' :
                    acct.status === 'default' ? 'bg-red-100 text-red-700' :
                    acct.status === 'frozen' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{acct.status}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
